// Date-proportional track grid used by the portfolio Gantt (one row per
// project, separate per-phase bars) and the project page (one row per phase).
// The individual Schedule retains its local Key and rendering. Portfolio-only
// presentation is opt-in; its shared ViewKey lives in the page toolbar.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { Popover } from "../../../components/Popover.jsx";
import { useHoverIntent } from "../../../components/useHoverIntent.js";
import { GANTT_LEGEND } from "./ganttModel.js";
import { ProjectsHeaderSurface, useProjectsScrollLayout } from "../ProjectsScrollLayout.jsx";
import { PortfolioTimeline } from "./PortfolioTimeline.jsx";

const MONTH_W = 56;
const ROW_H = 34;

// Tokens may use shorthand hex or CSS colors; never append alpha hex digits.
const APPROX_STRIPES = `repeating-linear-gradient(135deg, transparent 0 5px, ${T.whiteHalf} 5px 8px)`;

export function ScheduleTimeline({ rows, scale, today, nameWidth = 240, nameHeader = "PROJECT", testPrefix = "gantt", keyTestId, showCommencement = false, toolbar = null, placeholder = null, portfolio = false, density = "comfortable" }) {
  if (portfolio) return placeholder || <PortfolioTimeline rows={rows} scale={scale} today={today} density={density} />;
  return (
    <>
      {keyTestId && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: "1px solid var(--v2-hair)", flexWrap: "wrap" }}>
          <TimelineKey testId={keyTestId} showCommencement={showCommencement} />
          {toolbar}
        </div>
      )}
      {placeholder || <TimelineGrid rows={rows} scale={scale} today={today} nameWidth={nameWidth} nameHeader={nameHeader} testPrefix={testPrefix} />}
    </>
  );
}

function TimelineGrid({ rows, scale, today, nameWidth, nameHeader, testPrefix }) {
  const projectsLayout = useProjectsScrollLayout();
  const trackW = Math.max(scale.monthCount * MONTH_W, 480);
  if (projectsLayout) return (
    <ProjectsHeaderSurface testId="projects-gantt-scroll" header={
      <div style={{ display: "flex", minWidth: nameWidth + trackW }}>
        <div style={{ width: nameWidth, flexShrink: 0, position: "sticky", left: 0, zIndex: 2, height: 44, boxSizing: "border-box", borderRight: "1px solid var(--v2-line)", borderBottom: "1px solid var(--v2-line)", background: "var(--v2-tint)", display: "flex", alignItems: "flex-end", padding: "0 12px 6px", fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid, letterSpacing: ".07em" }}>{nameHeader}</div>
        <div style={{ width: trackW, flexShrink: 0 }}><TimelineAxis scale={scale} testPrefix={testPrefix} /></div>
      </div>
    }>
      <div data-testid={`${testPrefix}-scroll`} style={{ display: "flex", minWidth: nameWidth + trackW }}>
        <div style={{ width: nameWidth, flexShrink: 0, position: "sticky", left: 0, zIndex: 1, background: T.card, borderRight: "1px solid var(--v2-line)", boxSizing: "border-box" }}>
          {rows.map((r) => <TimelineName key={r.id} row={r} testPrefix={testPrefix} />)}
        </div>
        <div style={{ width: trackW, flexShrink: 0, position: "relative" }}>
          {rows.map((r) => <Track key={r.id} row={r} scale={scale} testPrefix={testPrefix} />)}
          <div data-testid={`${testPrefix}-today`} title="Today" style={{ position: "absolute", top: 0, bottom: 0, left: `${scale.pct(today)}%`, width: 2, background: T.red, pointerEvents: "none" }} />
        </div>
      </div>
    </ProjectsHeaderSurface>
  );
  return (
    <div style={{ display: "flex" }}>
      <div style={{ width: nameWidth, flexShrink: 0, borderRight: "1px solid var(--v2-line)" }}>
        <div style={{ height: 44, borderBottom: "1px solid var(--v2-line)", background: "var(--v2-tint)", display: "flex", alignItems: "flex-end", padding: "0 12px 6px", fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid, letterSpacing: ".07em" }}>{nameHeader}</div>
        {rows.map((r) => (
          <div key={r.id} style={{ height: ROW_H, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 12px", borderBottom: "1px solid var(--v2-hair)" }}>
            <span style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
              {r.swatch && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: r.swatch, flexShrink: 0 }} />}
              {r.name}
            </span>
            {r.sub != null && (
              <span data-testid={`${testPrefix}-phase-${r.id}`} style={{ fontSize: T.fsXxs, color: T.mid, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.sub}</span>
            )}
          </div>
        ))}
      </div>
      <div data-testid={`${testPrefix}-scroll`} style={{ overflowX: "auto", flex: 1 }}>
        <div style={{ width: trackW, position: "relative" }}>
          <TimelineAxis scale={scale} testPrefix={testPrefix} />
          {rows.map((r) => <Track key={r.id} row={r} scale={scale} testPrefix={testPrefix} />)}
          <div data-testid={`${testPrefix}-today`} title="Today" style={{ position: "absolute", top: 22, bottom: 0, left: `${scale.pct(today)}%`, width: 2, background: T.red, pointerEvents: "none" }} />
        </div>
      </div>
    </div>
  );
}

function TimelineAxis({ scale, testPrefix }) {
  return <>
    <div style={{ height: 22, boxSizing: "border-box", position: "relative", background: "var(--v2-tint)", borderBottom: "1px solid var(--v2-hair)" }}>
      {scale.quarters.map((q) => <div key={q.key} data-testid={`${testPrefix}-quarter-${q.key}`} style={{ position: "absolute", left: `${q.left}%`, width: `${q.width}%`, top: 0, bottom: 0, borderLeft: "1px solid var(--v2-line)", fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.dark, padding: "4px 6px", whiteSpace: "nowrap", overflow: "hidden" }}>{q.label}</div>)}
    </div>
    <div style={{ height: 22, boxSizing: "border-box", position: "relative", background: "var(--v2-tint)", borderBottom: "1px solid var(--v2-line)" }}>
      {scale.months.map((m) => <div key={m.key} style={{ position: "absolute", left: `${m.left}%`, width: `${m.width}%`, top: 0, bottom: 0, borderLeft: `1px solid ${m.yearStart ? T.mid : "var(--v2-hair)"}`, fontSize: T.fsXxs, color: T.mid, padding: "4px 5px", whiteSpace: "nowrap", overflow: "hidden" }}>{m.label}</div>)}
    </div>
  </>;
}

function TimelineName({ row: r, testPrefix }) {
  return <div style={{ height: ROW_H, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 12px", borderBottom: "1px solid var(--v2-hair)", boxSizing: "border-box" }}>
    <span style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
      {r.swatch && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: r.swatch, flexShrink: 0 }} />}{r.name}
    </span>
    {r.sub != null && <span data-testid={`${testPrefix}-phase-${r.id}`} style={{ fontSize: T.fsXxs, color: T.mid, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.sub}</span>}
  </div>;
}

function Track({ row, scale, testPrefix }) {
  return (
    <div data-testid={`${testPrefix}-row-${row.id}`} data-kind={row.kind} style={{ height: ROW_H, position: "relative", borderBottom: "1px solid var(--v2-hair)" }}>
      {row.bars.map((b) => (
        <div
          key={b.key}
          data-testid={`${testPrefix}-bar-${row.id}-${b.key}`}
          data-approx={b.approx ? "true" : "false"}
          title={`${b.label}: ${b.startDate} to ${b.endDate}${b.approx ? ` (approximate, unverified${b.source ? `: ${b.source}` : ""})` : ""}`}
          style={{ position: "absolute", top: 10, height: 14, borderRadius: 4, left: `${scale.pct(b.start)}%`, width: `max(${scale.pct(b.end) - scale.pct(b.start)}%, 3px)`, backgroundColor: b.color, backgroundImage: b.approx ? APPROX_STRIPES : "none", opacity: 0.92, boxShadow: `inset 0 0 0 1px ${T.whiteFade}`, transition: "left .25s ease, width .25s ease" }}
        />
      ))}
      {row.bars.length === 0 && row.emptyText && (
        <span data-testid={`${testPrefix}-nodates-${row.id}`} style={{ position: "sticky", left: 8, top: 0, lineHeight: "34px", paddingLeft: 8, fontSize: T.fsXxs, color: T.light, fontStyle: "italic" }}>{row.emptyText}</span>
      )}
      {row.markers.map((m) => (
        <span
          key={m.type}
          data-testid={`${testPrefix}-marker-${m.type}-${row.id}`}
          title={m.label}
          style={{ position: "absolute", top: m.type === "fdob" ? 4 : 22, left: `${scale.pct(m.t)}%`, marginLeft: -4, fontSize: T.fsXxxs, lineHeight: "9px", color: m.type === "fdob" ? T.dark : m.type === "commencement" ? T.projectsHubNavy : T.mid }}
        >
          {m.type === "fdob" ? "\u25C6" : m.type === "commencement" ? "\u25A1" : "\u25CF"}
        </span>
      ))}
    </div>
  );
}

export function TimelineKey({ testId = "gantt-key", showCommencement = false }) {
  const [pinned, setPinned] = useState(false);
  const intent = useHoverIntent({ interactive: true });
  const open = pinned || intent.open;
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const dismiss = intent.dismiss;
  const close = useCallback(() => { setPinned(false); dismiss(); }, [dismiss]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (rootRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
      close();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, close]);

  const rows = [
    ...GANTT_LEGEND.map((l) => ({ key: l.key, label: l.label, detail: "Recorded phase start to end", color: l.color, shape: "bar" })),
    { key: "today", label: "Today", detail: "Current date", color: T.red, shape: "tick" },
    { key: "fdob", label: "FDOB", detail: "First day of business", color: T.dark, shape: "diamond" },
    { key: "milestone", label: "Next milestone", detail: "Next open milestone due date", color: T.mid, shape: "circle" },
    ...(showCommencement ? [{ key: "commencement", label: "Commencement", detail: "Lease or works commencement", color: T.projectsHubNavy, shape: "square" }] : []),
  ];

  return (
    <span
      ref={rootRef}
      {...intent.triggerProps}
      onBlur={(e) => {
        intent.triggerProps.onBlur(e);
        if (!e.currentTarget.contains(e.relatedTarget)) close();
      }}
      style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
    >
      <button
        ref={triggerRef}
        type="button"
        data-testid={`${testId}-trigger`}
        aria-label="Gantt key"
        aria-expanded={open}
        aria-haspopup="dialog"
        onMouseDown={(e) => { intent.triggerProps.onMouseDown(e); e.stopPropagation(); }}
        onClick={() => {
          // A pointer may already have opened the hover preview. Its first
          // click pins the panel rather than immediately closing that preview.
          setPinned((value) => !value);
          intent.dismiss();
        }}
        style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 10px", border: `1px solid ${open ? T.borderDark : T.border}`, borderRadius: 999, background: open ? T.lightGray : T.white, color: open ? T.dark : T.light, fontSize: T.fsSm5, fontWeight: T.fwSemibold, lineHeight: 1, cursor: "help" }}
      >
        <span aria-hidden="true" style={{ display: "inline-flex", gap: 3 }}>
          {GANTT_LEGEND.map((l) => <span key={l.key} style={{ width: 7, height: 7, borderRadius: "50%", background: l.color }} />)}
        </span>
        <span>Key</span>
      </button>
      {open && (
        <Popover
          {...intent.popupProps}
          triggerRef={triggerRef}
          onClose={close}
          variant="menu"
          data-testid={`${testId}-popover`}
          aria-labelledby={`${testId}-title`}
          style={{ top: "calc(100% + 8px)", left: 0, width: 300, maxWidth: "calc(100vw - 24px)", padding: "14px 16px", border: "none", boxShadow: T.shadowTimelineKeyPopover }}
        >
          <span aria-hidden="true" style={{ position: "absolute", top: -6, left: 14, width: 10, height: 10, background: T.card, boxShadow: T.shadowTimelineKeyCaret, transform: "rotate(45deg)" }} />
          <div tabIndex={0} style={{ position: "relative" }}>
            <div id={`${testId}-title`} style={{ marginBottom: 10, color: T.dark, fontSize: T.fsSm5, fontWeight: T.fwBold }}>How to read this</div>
            <div style={{ display: "grid", gap: 8 }}>
              {rows.map((r) => (
                <div key={r.key} style={{ display: "grid", gridTemplateColumns: "26px 1fr", alignItems: "center", gap: 9, color: T.dark, fontSize: T.fsSm5 }}>
                  <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 16 }}>
                    {r.shape === "bar" && <span data-testid={`${testId}-swatch-${r.key}`} style={{ width: 22, height: 7, borderRadius: 3, background: r.color }} />}
                    {r.shape === "tick" && <span data-testid={`${testId}-swatch-${r.key}`} style={{ width: 2, height: 14, background: r.color }} />}
                    {r.shape === "diamond" && <span data-testid={`${testId}-swatch-${r.key}`} style={{ width: 9, height: 9, background: r.color, transform: "rotate(45deg)" }} />}
                    {r.shape === "circle" && <span data-testid={`${testId}-swatch-${r.key}`} style={{ width: 9, height: 9, borderRadius: "50%", background: r.color }} />}
                    {r.shape === "square" && <span data-testid={`${testId}-swatch-${r.key}`} style={{ width: 8, height: 8, border: `2px solid ${r.color}` }} />}
                  </span>
                  <span><strong style={{ fontWeight: T.fwSemibold }}>{r.label}</strong><span style={{ color: T.mid }}> - {r.detail}</span></span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 11, paddingTop: 10, borderTop: `1px solid ${T.hair}`, color: T.mid, fontSize: T.fsXs, lineHeight: 1.5 }}>
              Phases without both dates draw nothing. Hatched bars are approximate and not yet verified.
            </div>
          </div>
        </Popover>
      )}
    </span>
  );
}
